import {Table, Tag, Typography} from "antd";

const {Title, Paragraph, Text} = Typography;

/** Base URL API — hiển thị trong tài liệu (lấy từ cấu hình FE, fallback /api). */
const BASE_URL = process.env.REACT_APP_SERVICE_URL || '/api';

const pre = {
	background: 'var(--surface-muted, #f6f7f9)',
	border: '1px solid rgba(125,125,125,.18)',
	borderRadius: 8,
	padding: '10px 14px',
	fontSize: 13,
	overflowX: 'auto',
	whiteSpace: 'pre',
};

const ENDPOINTS = [
	{method: 'GET', path: '/v1/me', desc: 'Thông tin tài khoản + gói + hạn mức còn lại'},
	{method: 'GET', path: '/v1/projects', desc: 'Danh sách dự án'},
	{method: 'GET', path: '/v1/projects/{id}', desc: 'Chi tiết dự án + tóm tắt lần crawl mới nhất'},
	{method: 'GET', path: '/v1/projects/{id}/crawls', desc: 'Lịch sử crawl (điểm 6 nhóm, tối đa 50 lần)'},
	{method: 'GET', path: '/v1/projects/{id}/technical', desc: 'Báo cáo Kỹ thuật SEO (link hỏng, redirect, orphan, robots...)'},
	{method: 'GET', path: '/v1/seo-reports?domain=', desc: 'Lịch sử chấm điểm SEO đơn trang (lọc theo domain)'},
	{method: 'GET', path: '/v1/seo-reports/{id}', desc: 'Chi tiết 1 báo cáo (điểm nhóm + từng tiêu chí)'},
];

const EVENT_ROWS = [
	{event: 'crawl_done', desc: 'Crawl hoàn tất'},
	{event: 'crawl_failed', desc: 'Crawl thất bại (site không truy cập được)'},
	{event: 'gsc_backfill_done', desc: 'GSC: đồng bộ lịch sử hoàn tất'},
	{event: 'token_low', desc: 'AI token sắp cạn (<10%)'},
	{event: 'plan_changed', desc: 'Gói dịch vụ thay đổi'},
	{event: 'trend_alert', desc: 'Từ khoá trong kho lên trend'},
	{event: '…', desc: 'Webhook không chọn sự kiện nào sẽ nhận TẤT CẢ (kể cả loại mới thêm sau này)'},
];

/**
 * Tab "Tài liệu API" — hướng dẫn dùng API public v1 (X-API-Key) + xác minh chữ ký
 * webhook. Nội dung tĩnh, đồng bộ với routes api/v1 + WebhookSender ở backend.
 */
function ApiDocs() {
	return (
		<Typography style={{maxWidth: 860}}>
			<Title level={4}>Xác thực</Title>
			<Paragraph>
				Tạo API key ở tab <Text strong>API Keys</Text> (cần gói có tính năng API hệ thống), rồi gửi kèm
				mỗi request qua header <Text code>X-API-Key</Text>. Base URL: <Text code>{BASE_URL}</Text>.
			</Paragraph>
			<div style={pre}>{`curl -H "X-API-Key: <api_key_cua_ban>" \\
  "${BASE_URL}/v1/me"`}</div>
			<Paragraph style={{marginTop: 10}}>
				Mọi response bọc trong envelope chuẩn: <Text code>{'{"status":"success|error","code":200,"message":"...","data":{...}}'}</Text>.
				Key của gói không còn hỗ trợ API sẽ bị chặn <Text code>402</Text> (kể cả key tạo trước khi hạ gói).
			</Paragraph>

			<Title level={4}>Endpoint (v1 — chỉ đọc)</Title>
			<Table
				rowKey="path"
				size="small"
				pagination={false}
				dataSource={ENDPOINTS}
				columns={[
					{title: 'Method', dataIndex: 'method', width: 90, render: (m) => <Tag color="green">{m}</Tag>},
					{title: 'Đường dẫn', dataIndex: 'path', render: (p) => <Text code>{p}</Text>},
					{title: 'Mô tả', dataIndex: 'desc'},
				]}
				scroll={{x: 'max-content'}}
			/>
			<Paragraph style={{marginTop: 8}}>
				v1 chỉ trả dữ liệu ĐÃ CÓ (không khởi chạy crawl/phân tích mới) nên không trừ hạn mức URL kiểm tra
				hay AI token. Cần chạy tool → dùng giao diện web, kết quả đọc qua API.
			</Paragraph>

			<Title level={4}>Webhook — nhận sự kiện</Title>
			<Paragraph>
				Tạo webhook ở tab <Text strong>Webhooks</Text>: Omni SEO sẽ <Text code>POST</Text> JSON tới URL của bạn
				khi có sự kiện. Trả về HTTP 2xx trong 10 giây để xác nhận; lỗi sẽ được thử lại 5 lần
				(backoff 1′ → 5′ → 15′ → 1h → 4h); lỗi 10 lần liên tiếp thì webhook tự tắt.
			</Paragraph>
			<div style={pre}>{`POST <url_cua_ban>
Content-Type: application/json
X-OmniSeo-Event: crawl_done
X-OmniSeo-Delivery: 123
X-OmniSeo-Signature: t=1720000000,v1=5f3a...

{
  "event": "crawl_done",
  "created_at": "2026-07-06T10:00:00+07:00",
  "data": {
    "title": "Crawl hoàn tất: Website Sikido",
    "message": "Đã crawl 120 trang — điểm SEO trung bình 78/100.",
    "link": "/crawler/crawls/45"
  }
}`}</div>

			<Title level={5} style={{marginTop: 14}}>Sự kiện</Title>
			<Table
				rowKey="event"
				size="small"
				pagination={false}
				dataSource={EVENT_ROWS}
				columns={[
					{title: 'Event', dataIndex: 'event', width: 200, render: (e) => <Text code>{e}</Text>},
					{title: 'Mô tả', dataIndex: 'desc'},
				]}
				scroll={{x: 'max-content'}}
			/>

			<Title level={5} style={{marginTop: 14}}>Xác minh chữ ký (khuyến nghị)</Title>
			<Paragraph>
				Header <Text code>X-OmniSeo-Signature</Text> có dạng <Text code>t=&lt;unix&gt;,v1=&lt;hex&gt;</Text> với{' '}
				<Text code>v1 = HMAC-SHA256(t + "." + rawBody, secret)</Text> (secret xem ở nút chìa khoá của từng webhook).
				So khớp chữ ký + loại request có <Text code>t</Text> quá cũ (chống replay):
			</Paragraph>
			<div style={pre}>{`// Node.js
const crypto = require('crypto');

function verify(rawBody, header, secret) {
  const parts = Object.fromEntries(header.split(',').map(kv => kv.split('=')));
  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false; // > 5 phút
  const expected = crypto.createHmac('sha256', secret)
    .update(parts.t + '.' + rawBody).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(parts.v1));
}`}</div>
			<div style={{...pre, marginTop: 8}}>{`// PHP
function verify(string $rawBody, string $header, string $secret): bool {
    parse_str(str_replace(',', '&', $header), $parts);
    if (abs(time() - (int) $parts['t']) > 300) return false; // > 5 phút
    $expected = hash_hmac('sha256', $parts['t'] . '.' . $rawBody, $secret);
    return hash_equals($expected, (string) $parts['v1']);
}`}</div>
		</Typography>
	);
}

export default ApiDocs;
